import type { ViewerDraftSaveStatus } from "../../../types/viewerDeployment";

export interface ViewerDeploymentActionStateInput {
  enabled: boolean;
  companyId: string | null;
  productId: string | null;
  hasLiveDeployment: boolean;
  isPreviewPending: boolean;
  isDeployPending: boolean;
  hasActiveDeployJob: boolean;
  hasCurrentPreviewRelease: boolean;
  expectedDraftRevision: number | null;
  saveStatus: ViewerDraftSaveStatus;
}

export interface ViewerDeploymentActionState {
  previewLabel: "Preview" | "Update";
  canCreatePreview: boolean;
  canDeploy: boolean;
  previewBlockedMessage: string;
  deployBlockedMessage: string;
}

function resolvePreviewBlockedMessage(input: ViewerDeploymentActionStateInput) {
  if (input.saveStatus === "conflict") {
    return "Resolve the draft conflict before creating a preview.";
  }

  if (input.saveStatus === "saving") {
    return "Wait for the current draft save to finish before creating a preview.";
  }

  if (input.saveStatus === "failed") {
    return "Fix the draft save error before creating a preview.";
  }

  if (typeof input.expectedDraftRevision !== "number") {
    return "Viewer draft state is not ready for preview yet.";
  }

  return "Preview is not available right now.";
}

function resolveDeployBlockedMessage(input: ViewerDeploymentActionStateInput) {
  if (input.saveStatus === "conflict") {
    return "Resolve the draft conflict before deploying.";
  }

  if (input.saveStatus === "saving") {
    return "Wait for the current draft save to finish before deploying.";
  }

  if (input.saveStatus === "failed") {
    return "Fix the draft save error before deploying.";
  }

  if (input.hasActiveDeployJob) {
    return "A deploy job is already in progress for this project.";
  }

  if (!input.hasCurrentPreviewRelease) {
    return "Create a preview release before deploying.";
  }

  return "Deploy is not available right now.";
}

export function resolveViewerDeploymentActionState(
  input: ViewerDeploymentActionStateInput,
): ViewerDeploymentActionState {
  const previewLabel = input.hasLiveDeployment ? "Update" : "Preview";
  const hasProjectRoute = Boolean(input.companyId) && Boolean(input.productId);
  const canCreatePreview =
    input.enabled &&
    hasProjectRoute &&
    !input.isPreviewPending &&
    input.saveStatus !== "saving" &&
    input.saveStatus !== "conflict" &&
    input.saveStatus !== "failed" &&
    typeof input.expectedDraftRevision === "number";
  const canDeploy =
    input.enabled &&
    hasProjectRoute &&
    !input.isDeployPending &&
    !input.hasActiveDeployJob &&
    input.saveStatus !== "saving" &&
    input.saveStatus !== "conflict" &&
    input.saveStatus !== "failed" &&
    input.hasCurrentPreviewRelease;

  return {
    previewLabel,
    canCreatePreview,
    canDeploy,
    previewBlockedMessage: resolvePreviewBlockedMessage(input),
    deployBlockedMessage: resolveDeployBlockedMessage(input),
  };
}
